import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, Loader2, CheckCircle, AlertCircle, ScrollText, 
  ArrowLeft, Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MasterPartnerAgreement = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agreementData, setAgreementData] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  useEffect(() => {
    fetchAgreement();
  }, [token]);

  const fetchAgreement = async () => {
    try {
      const response = await axios.get(`${API}/master-partner/agreement/${token}`);
      setAgreementData(response.data);
      if (response.data.already_accepted) {
        setAccepted(true);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired agreement link');
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      setScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/master-partner/agreement/${token}/accept`);
      setAccepted(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to accept agreement');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading agreement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Agreement Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/master-partner/login"
            className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Agreement Accepted!</h1>
          <p className="text-gray-600 mb-6">
            Thank you for accepting the Master Partner Agreement. Your account is now fully activated and you can start recruiting partners.
          </p>
          <Link
            to="/master-partner/login"
            className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="p-4">
        <Link 
          to="/master-partner/login" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Login</span>
        </Link>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header Card */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 mb-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
              <ScrollText className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{agreementData?.agreement?.title || 'Master Partner Agreement'}</h1>
              <p className="text-purple-100 mt-1">
                {agreementData?.company_name} - {agreementData?.contact_person}
              </p>
              <p className="text-sm text-purple-200 mt-1">
                Version {agreementData?.agreement?.version}
              </p>
            </div>
          </div>
        </div>

        {/* Agreement Content */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b bg-gray-50">
            <h2 className="font-semibold text-gray-900">Please read the agreement carefully</h2>
            <p className="text-sm text-gray-500 mt-1">
              Scroll to the bottom to accept the agreement
            </p>
          </div>
          
          <div 
            className="p-6 max-h-[500px] overflow-y-auto prose prose-sm max-w-none"
            onScroll={handleScroll}
          >
            {agreementData?.agreement?.content ? (
              <div dangerouslySetInnerHTML={{ __html: agreementData.agreement.content }} />
            ) : (
              <p className="text-gray-500 italic">Agreement content not available</p>
            )}
          </div>

          <div className="p-6 border-t bg-gray-50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-500">
                {scrolledToBottom ? (
                  <span className="text-green-600 flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    You've read the agreement
                  </span>
                ) : (
                  'Please scroll to the bottom to read the entire agreement'
                )}
              </div>
              <button
                onClick={handleAccept}
                disabled={!scrolledToBottom || accepting}
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {accepting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    I Accept the Agreement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">
            <strong>Important:</strong> By accepting this agreement, you acknowledge that you have read, understood, and agree to be bound by the terms and conditions of the Master Partner Agreement. Once accepted, you will be able to recruit partners using your unique referral link.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MasterPartnerAgreement;
